import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Clock,
  CheckCircle,
  Calendar,
  Building2,
  HardHat,
  User,
  FileText,
  Plus,
  Search,
  Filter,
  RefreshCw,
  CreditCard,
  Upload,
} from 'lucide-react';
import {
  GlassCard,
  GlassButton,
  GlassInput,
  GlassSelect,
  GlassTable,
  GlassModal,
  GlassTextarea,
  FadeIn,
} from '../../components/ui';
import { api } from '../../hooks/useApi';

const BOOKS = {
  imobiliaria: { name: 'Imobiliaria', color: '#22c55e', icon: Building2 },
  construtora: { name: 'Construtora', color: '#3b82f6', icon: HardHat },
  pessoal: { name: 'Pessoal', color: '#8b5cf6', icon: User },
  geral: { name: 'Geral', color: '#94a3b8', icon: FileText },
};

const INITIAL_FORM = {
  valor: '',
  data_vencimento: new Date().toISOString().slice(0, 10),
  descricao: '',
  pessoa_nome: '',
  pessoa_tipo: 'fornecedor',
  numero_documento: '',
  conta_id: '',
  categoria_id: '',
  obra_id: '',
  imovel_id: '',
};

const openDocumentosOCR = (tipo = 'conta_pagar') => {
  const params = new URLSearchParams({ acao: 'importar', modulo: 'financeiro', tipo });
  window.location.assign(`/erp/documentos?${params.toString()}`);
};

const formatCurrency = (value) =>
  Number(value || 0).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });

const formatDate = (value) => {
  if (!value) return '-';
  return new Date(`${value}T00:00:00`).toLocaleDateString('pt-BR');
};

const daysUntil = (value) => {
  if (!value) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${value}T00:00:00`);
  return Math.ceil((due - today) / 86400000);
};

const normalizeText = (value) => String(value || '').toLowerCase();

const toOptions = (items, labelKeys = ['nome']) => (
  items || []
).map((item) => ({
  value: item.id,
  label: labelKeys.map((key) => item[key]).filter(Boolean).join(' - ') || item.id,
}));

export const FinanceiroContasPagar = () => {
  const [contas, setContas] = useState([]);
  const [summary, setSummary] = useState({ vencidas: 0, aVencer: 0, total: 0 });
  const [filters, setFilters] = useState({ book: 'all', status: 'all', search: '' });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfForm, setPdfForm] = useState(INITIAL_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importingPdf, setImportingPdf] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [options, setOptions] = useState({ contas: [], categorias: [], obras: [], imoveis: [] });
  const [error, setError] = useState('');

  const loadContas = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await api.get('/contabilidade/contas-a-pagar');
      const vencidas = (response.data.vencidas?.items || []).map((item) => ({ ...item, derived_status: 'atrasado' }));
      const aVencer = (response.data.a_vencer?.items || []).map((item) => ({ ...item, derived_status: 'pendente' }));
      setContas([...vencidas, ...aVencer]);
      setSummary({
        vencidas: vencidas.reduce((acc, item) => acc + Number(item.valor || 0), 0),
        aVencer: aVencer.reduce((acc, item) => acc + Number(item.valor || 0), 0),
        total: Number(response.data.total || 0),
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao carregar contas a pagar.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadContas();
  }, [loadContas]);

  useEffect(() => {
    const loadOptions = async () => {
      try {
        const [contasRes, categoriasRes, obrasRes, imoveisRes] = await Promise.all([
          api.get('/contabilidade/contas-bancarias'),
          api.get('/contabilidade/plano-contas', { params: { tipo: 'despesa' } }),
          api.get('/construtora/obras', { params: { per_page: 200 } }),
          api.get('/imobiliaria/imoveis', { params: { per_page: 200 } }),
        ]);
        setOptions({
          contas: toOptions(contasRes.data.items, ['nome', 'banco']),
          categorias: toOptions(categoriasRes.data.items, ['codigo', 'nome']),
          obras: toOptions(obrasRes.data.items, ['nome']),
          imoveis: toOptions(imoveisRes.data.items, ['codigo', 'titulo']),
        });
      } catch (err) {
        console.warn('Erro ao carregar opcoes de contas a pagar', err);
      }
    };
    loadOptions();
  }, []);

  const proximos7 = useMemo(() => {
    return contas
      .filter((item) => {
        const days = daysUntil(item.data_vencimento);
        return days >= 0 && days <= 7;
      })
      .reduce((acc, item) => acc + Number(item.valor || 0), 0);
  }, [contas]);

  const filteredData = useMemo(() => {
    return contas.filter((item) => {
      const book = item.book || 'geral';
      if (filters.book !== 'all' && book !== filters.book) return false;
      if (filters.status !== 'all' && item.derived_status !== filters.status) return false;
      if (filters.search) {
        const haystack = [
          item.descricao,
          item.pessoa_nome,
          item.numero_documento,
          item.categoria,
          item.conta,
        ].map(normalizeText).join(' ');
        if (!haystack.includes(normalizeText(filters.search))) return false;
      }
      return true;
    });
  }, [contas, filters]);

  const handleChange = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreate = async () => {
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...form,
        tipo: 'despesa',
        status: 'pendente',
        valor: Number(String(form.valor).replace(',', '.')),
      };
      if (payload.obra_id) payload.imovel_id = '';
      if (payload.imovel_id) payload.obra_id = '';
      await api.post('/contabilidade/movimentacoes', payload);
      setIsModalOpen(false);
      setForm(INITIAL_FORM);
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar conta a pagar.');
    } finally {
      setSaving(false);
    }
  };

  const handleImportPdf = async () => {
    if (!pdfFile) {
      setError('Selecione um PDF para importar.');
      return;
    }
    setImportingPdf(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('arquivo', pdfFile);
      const response = await api.post('/contabilidade/contas-a-pagar/importar-pdf', formData);
      const extracted = response.data.extracted || {};
      const nextForm = {
        ...INITIAL_FORM,
        valor: extracted.valor ? String(extracted.valor) : '',
        data_vencimento: extracted.data_vencimento || INITIAL_FORM.data_vencimento,
        descricao: extracted.descricao || INITIAL_FORM.descricao,
        pessoa_nome: extracted.pessoa_nome || '',
        pessoa_tipo: 'fornecedor',
        numero_documento: extracted.numero_documento || '',
      };
      setPdfPreview(response.data);
      setPdfForm(nextForm);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao importar PDF.');
    } finally {
      setImportingPdf(false);
    }
  };

  const handlePdfChange = (field, value) => {
    setPdfForm((current) => ({ ...current, [field]: value }));
  };

  const handleCreateFromPdf = async () => {
    if (!pdfPreview?.arquivo?.id) {
      setError('Importe e revise um PDF antes de salvar.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {
        ...pdfForm,
        arquivo_id: pdfPreview.arquivo.id,
        valor: Number(String(pdfForm.valor).replace(',', '.')),
      };
      if (payload.obra_id) payload.imovel_id = '';
      if (payload.imovel_id) payload.obra_id = '';
      await api.post('/contabilidade/contas-a-pagar/criar-de-arquivo', payload);
      setIsPdfModalOpen(false);
      setPdfFile(null);
      setPdfPreview(null);
      setPdfForm(INITIAL_FORM);
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao criar conta pelo PDF.');
    } finally {
      setSaving(false);
    }
  };

  const handlePay = async (row) => {
    setPayingId(row.id);
    setError('');
    try {
      await api.post(`/contabilidade/movimentacoes/${row.id}/pagar`, {
        data_pagamento: new Date().toISOString().slice(0, 10),
      });
      await loadContas();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Erro ao baixar conta a pagar.');
    } finally {
      setPayingId(null);
    }
  };

  const columns = [
    {
      key: 'descricao',
      title: 'Descricao',
      accessor: 'descricao',
      render: (row) => (
        <div>
          <p className="text-white/90">{row.descricao || row.pessoa_nome || 'Conta sem descricao'}</p>
          <p className="text-xs text-white/50">Venc: {formatDate(row.data_vencimento)}</p>
        </div>
      ),
    },
    {
      key: 'book',
      title: 'Book',
      accessor: 'book',
      render: (row) => {
        const book = BOOKS[row.book] || BOOKS.geral;
        return (
          <div className="flex items-center gap-2">
            {React.createElement(book.icon, {
              className: 'w-4 h-4',
              style: { color: book.color },
            })}
            <span style={{ color: book.color }}>{book.name}</span>
          </div>
        );
      },
    },
    {
      key: 'valor',
      title: 'Valor',
      accessor: 'valor',
      render: (row) => <span className="text-red-400 font-medium">{formatCurrency(row.valor)}</span>,
    },
    {
      key: 'status',
      title: 'Status',
      accessor: 'derived_status',
      render: (row) => {
        const overdue = row.derived_status === 'atrasado';
        const Icon = overdue ? AlertTriangle : Clock;
        return (
          <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
            overdue ? 'bg-red-500/20 text-red-400' : 'bg-yellow-500/20 text-yellow-400'
          }`}>
            <Icon className="w-3 h-3" />
            {overdue ? 'Atrasado' : 'Pendente'}
          </span>
        );
      },
    },
    {
      key: 'alerta',
      title: 'Alerta',
      render: (row) => {
        const days = daysUntil(row.data_vencimento);
        if (days < 0) {
          return (
            <span className="flex items-center gap-1 text-red-400 text-xs">
              <AlertTriangle className="w-4 h-4" />
              {Math.abs(days)} dias atrasado
            </span>
          );
        }
        if (days <= 7) {
          return (
            <span className="flex items-center gap-1 text-yellow-400 text-xs">
              <Calendar className="w-4 h-4" />
              Vence em {days} dias
            </span>
          );
        }
        return <span className="text-white/40 text-xs">-</span>;
      },
    },
    {
      key: 'acoes',
      title: 'Acoes',
      render: (row) => (
        <GlassButton
          size="xs"
          variant="success"
          icon={CreditCard}
          loading={payingId === row.id}
          onClick={(event) => {
            event.stopPropagation();
            handlePay(row);
          }}
        >
          Baixar
        </GlassButton>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-white/90">Contas a Pagar</h1>
          <p className="text-white/50">
            {loading ? 'Carregando obrigacoes reais...' : `${filteredData.length} obrigacoes em aberto`}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <GlassButton variant="secondary" icon={RefreshCw} onClick={loadContas} loading={loading}>
            Atualizar
          </GlassButton>
          <GlassButton variant="secondary" icon={Upload} onClick={() => openDocumentosOCR('conta_pagar')}>
            Enviar conta/nota
          </GlassButton>
          <GlassButton variant="secondary" icon={Upload} onClick={() => setIsPdfModalOpen(true)}>
            Importar PDF
          </GlassButton>
          <GlassButton icon={Plus} onClick={() => setIsModalOpen(true)}>
            Nova conta
          </GlassButton>
        </div>
      </div>

      {error && (
        <GlassCard className="p-4 border-red-500/30 bg-red-500/10" hover={false}>
          <p className="text-sm text-red-300">{error}</p>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <GlassCard className="p-6 border-yellow-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">A vencer</p>
              <p className="text-2xl font-bold text-yellow-400">{formatCurrency(summary.aVencer)}</p>
            </div>
            <div className="p-3 rounded-xl bg-yellow-500/20">
              <Clock className="w-6 h-6 text-yellow-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6 border-red-500/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Vencido</p>
              <p className="text-2xl font-bold text-red-400">{formatCurrency(summary.vencidas)}</p>
            </div>
            <div className="p-3 rounded-xl bg-red-500/20">
              <AlertTriangle className="w-6 h-6 text-red-400" />
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white/50">Proximos 7 dias</p>
              <p className="text-2xl font-bold text-white/90">{formatCurrency(proximos7)}</p>
            </div>
            <div className="p-3 rounded-xl bg-accent-primary/20">
              <Calendar className="w-6 h-6 text-accent-primary" />
            </div>
          </div>
        </GlassCard>
      </div>

      <FadeIn>
        <GlassCard className="p-4">
          <div className="flex items-center gap-4 flex-wrap">
            <GlassInput
              icon={Search}
              placeholder="Buscar conta..."
              className="max-w-xs"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
            />

            <GlassSelect
              value={filters.book}
              onChange={(e) => setFilters({ ...filters, book: e.target.value })}
              placeholder=""
              className="min-w-[170px]"
              options={[
                { value: 'all', label: 'Todos os books' },
                { value: 'imobiliaria', label: 'Imobiliaria' },
                { value: 'construtora', label: 'Construtora' },
                { value: 'pessoal', label: 'Pessoal' },
                { value: 'geral', label: 'Geral' },
              ]}
            />

            <GlassSelect
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value })}
              placeholder=""
              className="min-w-[150px]"
              options={[
                { value: 'all', label: 'Todos os status' },
                { value: 'pendente', label: 'Pendente' },
                { value: 'atrasado', label: 'Atrasado' },
              ]}
            />

            <GlassButton variant="ghost" icon={Filter}>
              Filtros
            </GlassButton>
          </div>
        </GlassCard>
      </FadeIn>

      <FadeIn delay={0.2}>
        <GlassCard>
          <GlassTable
            columns={columns}
            data={filteredData}
            searchable={false}
            emptyMessage={loading ? 'Carregando contas...' : 'Nenhuma conta a pagar em aberto'}
          />
        </GlassCard>
      </FadeIn>

      <GlassModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title="Nova Conta a Pagar"
        icon={Plus}
        size="lg"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton onClick={handleCreate} loading={saving}>
              Salvar
            </GlassButton>
          </>
        }
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <GlassInput
            label="Valor"
            type="number"
            min="0"
            step="0.01"
            value={form.valor}
            onChange={(e) => handleChange('valor', e.target.value)}
            placeholder="0,00"
          />
          <GlassInput
            label="Vencimento"
            type="date"
            value={form.data_vencimento}
            onChange={(e) => handleChange('data_vencimento', e.target.value)}
          />
          <div className="md:col-span-2">
            <GlassTextarea
              label="Descricao"
              rows={3}
              value={form.descricao}
              onChange={(e) => handleChange('descricao', e.target.value)}
              placeholder="Ex.: fornecedor, aluguel, imposto..."
            />
          </div>
          <GlassSelect
            label="Conta bancaria"
            value={form.conta_id}
            onChange={(e) => handleChange('conta_id', e.target.value)}
            placeholder="Sem conta"
            options={options.contas}
          />
          <GlassSelect
            label="Plano de contas"
            value={form.categoria_id}
            onChange={(e) => handleChange('categoria_id', e.target.value)}
            placeholder="Sem categoria"
            options={options.categorias}
          />
          <GlassInput
            label="Fornecedor"
            value={form.pessoa_nome}
            onChange={(e) => handleChange('pessoa_nome', e.target.value)}
            placeholder="Nome do fornecedor"
          />
          <GlassSelect
            label="Tipo de pessoa"
            value={form.pessoa_tipo}
            onChange={(e) => handleChange('pessoa_tipo', e.target.value)}
            placeholder=""
            options={[
              { value: 'fornecedor', label: 'Fornecedor' },
              { value: 'funcionario', label: 'Funcionario' },
              { value: 'colaborador', label: 'Colaborador' },
            ]}
          />
          <GlassInput
            label="Documento"
            value={form.numero_documento}
            onChange={(e) => handleChange('numero_documento', e.target.value)}
            placeholder="NF, boleto, recibo..."
          />
          <GlassSelect
            label="Obra"
            value={form.obra_id}
            onChange={(e) => setForm((current) => ({ ...current, obra_id: e.target.value, imovel_id: e.target.value ? '' : current.imovel_id }))}
            placeholder="Sem obra"
            options={options.obras}
          />
          <GlassSelect
            label="Imovel"
            value={form.imovel_id}
            onChange={(e) => setForm((current) => ({ ...current, imovel_id: e.target.value, obra_id: e.target.value ? '' : current.obra_id }))}
            placeholder="Sem imovel"
            options={options.imoveis}
          />
        </div>
      </GlassModal>

      <GlassModal
        isOpen={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        title="Importar Conta por PDF"
        icon={Upload}
        size="xl"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsPdfModalOpen(false)}>
              Cancelar
            </GlassButton>
            <GlassButton
              onClick={handleCreateFromPdf}
              loading={saving}
              disabled={!pdfPreview?.arquivo?.id}
            >
              Criar conta pendente
            </GlassButton>
          </>
        }
      >
        <div className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4 items-end">
            <GlassInput
              label="PDF"
              type="file"
              accept=".pdf,application/pdf"
              onChange={(e) => {
                setPdfFile(e.target.files?.[0] || null);
                setPdfPreview(null);
              }}
            />
            <GlassButton icon={Upload} onClick={handleImportPdf} loading={importingPdf}>
              Ler PDF
            </GlassButton>
          </div>

          {pdfPreview?.extracted && (
            <GlassCard className="p-4 bg-white/[0.02]" hover={false}>
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-white/50">Confianca da leitura</p>
                  <p className="text-xl font-semibold text-white/90">{pdfPreview.extracted.confidence || 0}%</p>
                </div>
                <p className="text-xs text-white/40 text-right max-w-md">
                  Revise os campos antes de salvar. PDFs escaneados podem exigir OCR no proximo bloco.
                </p>
              </div>
            </GlassCard>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label="Valor"
              type="number"
              min="0"
              step="0.01"
              value={pdfForm.valor}
              onChange={(e) => handlePdfChange('valor', e.target.value)}
              placeholder="0,00"
            />
            <GlassInput
              label="Vencimento"
              type="date"
              value={pdfForm.data_vencimento}
              onChange={(e) => handlePdfChange('data_vencimento', e.target.value)}
            />
            <GlassInput
              label="Fornecedor"
              value={pdfForm.pessoa_nome}
              onChange={(e) => handlePdfChange('pessoa_nome', e.target.value)}
              placeholder="Nome do fornecedor"
            />
            <GlassInput
              label="Documento"
              value={pdfForm.numero_documento}
              onChange={(e) => handlePdfChange('numero_documento', e.target.value)}
              placeholder="NF, boleto, recibo..."
            />
            <GlassSelect
              label="Conta bancaria"
              value={pdfForm.conta_id}
              onChange={(e) => handlePdfChange('conta_id', e.target.value)}
              placeholder="Sem conta"
              options={options.contas}
            />
            <GlassSelect
              label="Plano de contas"
              value={pdfForm.categoria_id}
              onChange={(e) => handlePdfChange('categoria_id', e.target.value)}
              placeholder="Sem categoria"
              options={options.categorias}
            />
            <GlassSelect
              label="Obra"
              value={pdfForm.obra_id}
              onChange={(e) => setPdfForm((current) => ({ ...current, obra_id: e.target.value, imovel_id: e.target.value ? '' : current.imovel_id }))}
              placeholder="Sem obra"
              options={options.obras}
            />
            <GlassSelect
              label="Imovel"
              value={pdfForm.imovel_id}
              onChange={(e) => setPdfForm((current) => ({ ...current, imovel_id: e.target.value, obra_id: e.target.value ? '' : current.obra_id }))}
              placeholder="Sem imovel"
              options={options.imoveis}
            />
            <div className="md:col-span-2">
              <GlassTextarea
                label="Descricao"
                rows={3}
                value={pdfForm.descricao}
                onChange={(e) => handlePdfChange('descricao', e.target.value)}
                placeholder="Descricao revisavel da conta"
              />
            </div>
          </div>

          {pdfPreview?.extracted?.texto_preview && (
            <div className="rounded-lg border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-wide text-white/40 mb-2">Texto lido</p>
              <p className="text-xs text-white/60 whitespace-pre-wrap max-h-40 overflow-auto">
                {pdfPreview.extracted.texto_preview}
              </p>
            </div>
          )}
        </div>
      </GlassModal>
    </div>
  );
};

export default FinanceiroContasPagar;
